import { Button } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { Navigation, ZoomIn, ZoomOut } from '@material-ui/icons';
import React, { useCallback, useMemo } from 'react';
import { getNiceTickValues } from 'recharts-scale';
import { TICKS_COUNT } from '../Graph/Graph.utils';
import { isNil } from 'lodash';

export enum ArrowDir {
  Top,
  Left,
  Right,
  Bottom,
}

const PAN_DATES_FACTOR = 32;
const DATA_ROUND_FACTOR = Math.pow(10, 10);

interface GraphControlsProps {
  plotRef: React.MutableRefObject<uPlot>;
  /**
   * `dataTestId` - name of a Graph Controls (preferably `<graph_name>-controls`)
   * @example
   * ```tsx
   * <GraphControls {...props} dataTestId="test-graph-controls" />
   */
  dataTestId: string;
}

export const GraphControls: React.FC<GraphControlsProps> = ({ plotRef, dataTestId }) => {
  const clearZoom = useCallback(() => {
    plotRef?.current?.root?.getElementsByClassName('u-over')?.[0]?.dispatchEvent(
      new MouseEvent('dblclick', {
        view: window,
        bubbles: true,
        cancelable: true,
      })
    );
  }, [plotRef]);

  const movingFunc = useCallback(
    (i: ArrowDir) => {
      const u = plotRef?.current;
      if (!u) return;

      const oxRange = u.scales.x.max - u.scales.x.min;

      u.batch(() => {
        u.setScale('x', {
          min:
            u.scales.x.min +
            (i === ArrowDir.Left || i === ArrowDir.Right
              ? (i === ArrowDir.Right ? oxRange : -oxRange) / PAN_DATES_FACTOR
              : 0),
          max:
            u.scales.x.max +
            (i === ArrowDir.Left || i === ArrowDir.Right
              ? (i === ArrowDir.Right ? oxRange : -oxRange) / PAN_DATES_FACTOR
              : 0),
        });

        Object.entries(u.scales)
          ?.filter(([key]) => key !== 'x')
          ?.forEach(([key, scale]) => {
            const notToUseTicks = key === 'y' || isNil(scale.min) || isNil(scale.max);

            const ticks = notToUseTicks ? [0] : getNiceTickValues([scale.min, scale.max], TICKS_COUNT, true);

            const range = notToUseTicks ? 0 : Math.round((ticks[1] - ticks[0]) * DATA_ROUND_FACTOR) / DATA_ROUND_FACTOR;

            const newMin =
              Math.round(
                (ticks?.[0] +
                  (i === ArrowDir.Top || i === ArrowDir.Bottom ? (i === ArrowDir.Top ? range : -range) : 0)) *
                  DATA_ROUND_FACTOR
              ) / DATA_ROUND_FACTOR;

            const newMax =
              Math.round(
                (ticks?.[ticks?.length - 1] +
                  (i === ArrowDir.Top || i === ArrowDir.Bottom ? (i === ArrowDir.Top ? range : -range) : 0)) *
                  DATA_ROUND_FACTOR
              ) / DATA_ROUND_FACTOR;

            u.setScale(key, {
              min: newMin,
              max: newMax,
            });
          });
      });
    },
    [plotRef]
  );

  const zoomGraph = useCallback(
    (zoomIn: boolean) => {
      const u = plotRef?.current;
      if (!u) return;

      const oxRange = u.scales.x.max - u.scales.x.min;

      u.batch(() => {
        u.setScale('x', {
          min: u.scales.x.min + (zoomIn ? oxRange : -oxRange) / PAN_DATES_FACTOR,
          max: u.scales.x.max + (zoomIn ? -oxRange : oxRange) / PAN_DATES_FACTOR,
        });
        Object.entries(u.scales)
          ?.filter(([key]) => key !== 'x')
          .forEach(([key, scale]) => {
            const notToUseTicks = key === 'y' || isNil(scale.min) || isNil(scale.max);

            const ticks = notToUseTicks ? [0] : getNiceTickValues([scale.min, scale.max], TICKS_COUNT, true);

            const range = notToUseTicks ? 0 : Math.round((ticks[1] - ticks[0]) * DATA_ROUND_FACTOR) / DATA_ROUND_FACTOR;

            u.setScale(key, {
              min: ticks?.[0] + (zoomIn ? range : -range),
              max: ticks?.[ticks?.length - 1] + (zoomIn ? -range : range),
            });
          });
      });
    },
    [plotRef]
  );

  const resetBtn = useMemo(
    () => (
      <Button
        variant="outlined"
        style={{ width: 80, height: 28, marginBottom: 4 }}
        onClick={() => clearZoom?.()}
        dataTestId={`${dataTestId}-reset-zoom-button`}
      >
        <Trans>Reset zoom</Trans>
      </Button>
    ),
    [dataTestId, clearZoom]
  );

  const zoomButtons = useMemo(
    () => (
      <div
        className="flex flex-row justify-between text-primaryColor hover:text-opacity-90 transition cursor-pointer mb-1 px-1.5"
        style={{ width: 80 }}
        data-testid={`${dataTestId}-zoom-buttons`}
      >
        <ZoomIn onClick={() => zoomGraph(true)} data-testid={`${dataTestId}-zoom-in-button`} />
        <ZoomOut onClick={() => zoomGraph(false)} data-testid={`${dataTestId}-zoom-out-button`} />
      </div>
    ),
    [dataTestId, zoomGraph]
  );

  const arrows = useMemo(() => {
    const classes = 'text-primaryColor cursor-pointer hover:text-opacity-90 transition sm:h-5';
    return (
      <div className="flex flex-col items-center" style={{ width: 80 }} data-testid={`${dataTestId}-arrows`}>
        <div
          className={classes}
          onClick={() => movingFunc(ArrowDir.Top)}
          data-testid={`${dataTestId}-arrow-top-button`}
        >
          <Navigation />
        </div>
        <div className="flex justify-between w-full px-1">
          <div
            className={classes}
            onClick={() => movingFunc(ArrowDir.Left)}
            data-testid={`${dataTestId}-arrow-left-button`}
          >
            <Navigation style={{ transform: 'rotate(270deg)' }} />
          </div>
          <div
            className={classes}
            onClick={() => movingFunc(ArrowDir.Right)}
            data-testid={`${dataTestId}-arrow-right-button`}
          >
            <Navigation style={{ transform: 'rotate(90deg)' }} />
          </div>
        </div>
        <div
          className={classes}
          onClick={() => movingFunc(ArrowDir.Bottom)}
          data-testid={`${dataTestId}-arrow-bottom-button`}
        >
          <Navigation style={{ transform: 'rotate(180deg)' }} />
        </div>
      </div>
    );
  }, [dataTestId, movingFunc]);

  return (
    <div
      className="flex flex-col sm:flex-row items-end rounded-md pb-1"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
      data-testid={`${dataTestId}-wrapper`}
    >
      <div className="flex flex-col">
        {resetBtn}
        {zoomButtons}
      </div>
      {arrows}
    </div>
  );
};
